import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import BoardSearch from "./BoardSearch";
import { ActivityButton } from "./ActivityPanel";
import CreateExecutionWizard from "./CreateExecutionWizard";
import Loader from "./Loader";
import { DIALOG_HEIGHT } from "./dialogSize";
import RunView, { CredentialsPromptModal, DynamicPromptModal } from "./RunView";
import { GherkinLines, NO_SCENARIO, StatusBadge, XrayTestSteps, isGherkin, scenarioCount } from "./TestCaseRow";
import ChatMarkdown from "./ChatMarkdown";
import { issueStatusNote, JiraStatusBadge, PriorityBadge, trialWarning, TRIAL_LABEL, EVIDENCE_POINTER } from "../jiraBadges";
import { defaultProjectOf } from "../defaultProject";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* ws:// URL for a run stream (mirrors BoardDetail). */
function apiWsUrl(path) {
  const base = new URL(API_BASE_URL, window.location.origin);
  const proto = base.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${base.host}${path}`;
}

/* Human-readable one-liner for a step (mirrors BoardDetail.describeStep). */
/* Steps that are a whole interaction rather than one action on one element —
   they carry no selector, so the generic "action + selector" form reads badly. */
const WHOLE_STEP_LABELS = {
  esignSingle: "Complete the eSignature (sign as the logged-in user)",
  esignDouble: "Complete the eSignature (logged-in user, then verifier)",
  esign: "Complete the eSignature dialog",
  closeDialog: "Close the dialog and return to the grid",
};

function describeStep(s) {
  if (s.description) return s.description;
  if (WHOLE_STEP_LABELS[s.action]) return WHOLE_STEP_LABELS[s.action];
  const bits = [s.action];
  if (s.selector) bits.push(s.selector);
  if (s.value != null && s.value !== "") bits.push(`= "${s.value}"`);
  return bits.join(" ");
}

/* How a test case was created, for the row's provenance badge. */
function resolveCreatedVia(source) {
  const src = source || {};
  if (src.creation === "ai" || src.creation === "manual") return src.creation;
  if (src.category || src.connectTo || src.jiraKey || src.jiraTicket || src.changelogCommit || src.changelogVersion) {
    return "ai";
  }
  if (src.url || src.recorded_total != null) return "manual";
  return null;
}

/* "Artifact by <user> · <date>" for a generated script. The creator is the
   Cortex user who saved the case (TestCase.userkey); older rows saved without a
   user show only the date. */
function ScriptAuthor({ tc }) {
  if (!tc || (!tc.createdBy && !tc.createdAt)) return null;
  const d = tc.createdAt ? new Date(tc.createdAt) : null;
  const when = d && !Number.isNaN(d.getTime())
    ? d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";
  return (
    <span
      className="flex-none text-[11px] font-normal text-slate-500"
      title={`Artifact created${tc.createdBy ? ` by ${tc.createdBy}` : ""}${when ? ` on ${when}` : ""}`}
    >
      Artifact by{" "}
      <span className="font-medium text-slate-700">{tc.createdBy || "unknown user"}</span>
      {when && <> · {when}</>}
    </span>
  );
}

/* Turn an API test case into the shape TestCaseRow + RunView expect — the same
   mapping BoardDetail uses, so the row shows the Xray↔Playwright grid, per-step
   pass/fail from the last run, and lazy-loaded step screenshots. */
function fromApi(t) {
  const rawSteps = Array.isArray(t.steps) ? t.steps : [];
  const lastSteps = Array.isArray(t.last_run?.steps) ? t.last_run.steps : [];
  return {
    id: t.testcasekey,
    testcasekey: t.testcasekey,
    boardId: t.board_id,
    subcategoryKey: t.subcategorykey || null,
    groupKey: t.groupkey || null,
    name: t.name,
    priority: t.priority || "Medium",
    status: t.last_run?.status || t.status || "pending",
    menuLabel: t.menu_label,
    menuPath: t.menu_path || [],
    source: t.source || {},
    aiContext: t.ai_context || "",
    createdBy: t.created_by || null,
    /* When this version of the script was generated: an Artifact created
       again is replaced in place, so createddate is when the case first
       existed, not when its current steps were authored. */
    createdAt: t.source?.generated_at || t.createddate || null,
    createdVia: resolveCreatedVia(t.source),
    lastRun: t.last_run || null,
    linkedTickets: Array.isArray(t.linked_tickets) ? t.linked_tickets : [],
    steps: rawSteps.map((s, i) => {
      const prev = lastSteps[i] || null;
      const shotUrl = prev?.screenshot
        ? `${API_BASE_URL}testcases/${t.testcasekey}/steps/${i + 1}/screenshot?v=${encodeURIComponent(t.last_run?.at || "")}`
        : null;
      return {
        name: describeStep(s),
        status: prev?.status || "pending",
        action: s.action,
        selector: s.selector,
        value: s.value,
        x_ratio: s.x_ratio ?? null,
        y_ratio: s.y_ratio ?? null,
        text: s.text ?? null,
        description: s.description ?? null,
        field_label: s.field_label ?? null,
        kind: s.kind ?? null,
        masked: !!s.masked,
        is_dynamic: !!s.is_dynamic,
        param_name: s.param_name ?? null,
        capture_screenshot: !!s.capture_screenshot,
        constraints: s.constraints ?? null,
        source_step_no: s.source_step_no ?? null,
        screenshot: shotUrl,
        error: prev?.error || null,
      };
    }),
  };
}


/* Xray test summaries are usually authored with their story's key in front of
   them — "SCRUM-350: Setting a new default workcenter…" — because the drafting
   agent is handed the story front and centre in its brief. On this screen that
   key is already in the page header, and the test's own key sits right beside
   the title, so the prefix is noise that pushes the real title out of view.
   Stripped for display only; nothing in Jira is touched. */
const KEY_PREFIX = /^\s*([A-Z][A-Z0-9_]*)-\d+\s*[:\u2013\u2014-]\s*/;

function plainTitle(text, projectKey) {
  const raw = String(text || "").trim();
  const m = raw.match(KEY_PREFIX);
  if (!m) return raw;
  /* Only strip a key belonging to this project. "ISO-9001: Compliance" is a
     title in its own right, not a prefixed one — unless the project really is
     called ISO. */
  const proj = String(projectKey || "").split("-")[0].toUpperCase();
  if (!proj || m[1].toUpperCase() !== proj) return raw;
  return raw.slice(m[0].length).trim() || raw;
}


/* ------------------------------------------------------------------ */
/*  Column shell                                                       */
/* ------------------------------------------------------------------ */
/* A column-header "+" with a built-in hover hint. The hint doubles as the
   disabled explanation — it always tells the user what the button will do or
   what they must select first to enable it. */
function AddButton({ onClick, disabled = false, hint }) {
  return (
    <span className="group/add relative flex-none">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={hint}
        className={[
          "flex h-7 w-7 items-center justify-center rounded-lg transition",
          disabled
            ? "cursor-not-allowed border border-dashed border-slate-300 bg-white text-slate-300"
            : "bg-brand-600 text-white shadow-sm ring-1 ring-brand-700/10 hover:-translate-y-px hover:bg-brand-700 hover:shadow-md active:translate-y-0",
        ].join(" ")}
      >
        {disabled ? <Icons.Lock className="h-3.5 w-3.5" /> : <Icons.Plus className="h-4 w-4" />}
      </button>
      {hint && (
        <span className="pointer-events-none absolute right-0 top-full z-40 mt-2 w-max max-w-[220px] rounded-lg bg-slate-900 px-2.5 py-1.5 text-left text-[11px] font-medium leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/add:opacity-100">
          <span className="absolute -top-1 right-2.5 h-2 w-2 rotate-45 bg-slate-900" />
          {disabled && (
            <span className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
              <Icons.Lock className="h-3 w-3" /> Locked
            </span>
          )}
          {hint}
        </span>
      )}
    </span>
  );
}

export function Column({
  icon: Icon,
  title,
  count,
  accent = "slate",
  children,
  footer,
  onAdd,
  addDisabled = false,
  addTitle,
  subHeader,
  busy = false,
  onSort,
  sortAsc = true,
  /* Jira-style issue-type icons (JiraEpic / JiraStory / JiraTask / JiraXrayTest)
     already render as a self-contained coloured square, so wrapping them in
     the accent badge below produces a nested-square look. Call sites pass
     plainIcon so the icon stands alone. iconColor overrides the accent's own
     colour — used to keep the Jira-brand palette (purple Epic, green Story,
     teal Xray Test) even while the rest of the app runs on a blue accent. */
  plainIcon = false,
  iconColor,
  /* Optional "Enter →" affordance at the right end of the header, used for
     the Tests column so a user can jump to the current story's full detail
     view without a per-row chevron. */
  onEnter,
  enterDisabled = false,
  enterTitle = "Open full detail",
}) {
  const accents = {
    blue: "text-blue-700 bg-blue-50 ring-blue-600/15",
    emerald: "text-emerald-700 bg-emerald-50 ring-emerald-600/15",
    slate: "text-slate-700 bg-slate-100 ring-slate-500/15",
  };
  const plainIconColor = {
    blue: "text-blue-600",
    emerald: "text-emerald-600",
    slate: "text-slate-600",
  };
  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-slate-50/60 shadow-sm">
      <header className="flex items-center gap-2 border-b border-slate-200 px-3 py-2.5">
        {plainIcon ? (
          <Icon className={`h-5 w-5 flex-none ${iconColor || plainIconColor[accent] || "text-slate-600"}`} />
        ) : (
          <span className={`flex h-6 w-6 flex-none items-center justify-center rounded-lg ring-1 ${accents[accent]}`}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{title}</h3>
        {/* Reloading over a list that is already on screen: a spinner in the
            header says so without blanking the list the user is reading. */}
        {busy && (
          <span className="flex-none text-slate-400" title="Reloading…" aria-label="Reloading">
            <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
          </span>
        )}
        {typeof count === "number" && (
          <span className="flex-none rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-500 ring-1 ring-slate-200">
            {count}
          </span>
        )}
        {onSort && (
          <button
            type="button"
            onClick={onSort}
            title={
              sortAsc
                ? "Sorted by ID, ascending — click for descending"
                : "Sorted by ID, descending — click for ascending"
            }
            className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            {sortAsc ? <Icons.SortAsc className="h-3.5 w-3.5" /> : <Icons.SortDesc className="h-3.5 w-3.5" />}
          </button>
        )}
        {onAdd && <AddButton onClick={onAdd} disabled={addDisabled} hint={addTitle} />}
        {onEnter && (
          <button
            type="button"
            onClick={onEnter}
            disabled={enterDisabled}
            title={enterTitle}
            className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-300"
          >
            Review <Icons.ChevronRight className="h-4 w-4" />
          </button>
        )}
      </header>
      {subHeader}
      <div className="cq-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">{children}</div>
      {footer}
    </section>
  );
}

export function EmptyState({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      <Icon className="h-6 w-6 text-slate-300" />
      <p className="text-xs text-slate-400">{text}</p>
    </div>
  );
}

function Loading({ text = "Loading…", hide = false }) {
  // `hide` lets a column suppress its own inline loader while the full-screen
  // guide popup is up — without it the spark would bleed through the backdrop
  // blur and compete with the popup's own artwork.
  if (hide) return null;
  return <Loader.Block label={text} sublabel="" className="py-6" />;
}



/* ================================================================== */
/*  Story detail — the story's Xray tests (each with the Xray↔Playwright */
/*  mapping grid) and its executions, on their own screen.             */
/* ================================================================== */
function StoryDetail({
  story,
  xrayTests,
  executions,
  coverageError,
  coverageLoading,
  executionsLoading,
  userkey,
  onBack,
  onCreateXray,
  onCreatePlaywright,
  onCreateExecution,
  execDialog,
}) {
  // Full test cases (mapped like BoardDetail so TestCaseRow shows the Xray↔
  // Playwright grid + per-step results + screenshots), each tagged with its
  // Xray key for grouping. Run state mirrors BoardDetail's engine.
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [running, setRunning] = useState(() => new Set());
  /* Which Xray test cards are open. Collapsed by default: a story with three
     tests and six steps each is several screens of scrolling before you reach
     the executions, and most of the time you only want one of them. */
  const [openTests, setOpenTests] = useState(() => new Set());
  const [viewCaseId, setViewCaseId] = useState(null);
  const [pendingPrompts, setPendingPrompts] = useState({});
  const [pendingCredentials, setPendingCredentials] = useState({});
  const wsRefs = useRef({});
  const runAppRejected = useRef({});

  const tests = xrayTests || [];

  /* Each test's "How To Test" field from Jira (Markdown), loaded once for all
     the story's tests when this screen opens — the board itself never needs
     it, so it stays out of the coverage call. */
  const [howToTest, setHowToTest] = useState({ loading: false, items: {}, found: true, error: "" });
  const testKeysParam = tests.map((x) => x.key).filter(Boolean).join(",");
  useEffect(() => {
    if (!testKeysParam) return undefined;
    let alive = true;
    setHowToTest((h) => ({ ...h, loading: true, error: "" }));
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}jira/how-to-test?keys=${encodeURIComponent(testKeysParam)}`);
        const body = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
        setHowToTest({
          loading: false,
          items: body?.data?.items || {},
          found: body?.data?.field_found !== false,
          error: "",
        });
      } catch (e) {
        if (alive) setHowToTest({ loading: false, items: {}, found: true, error: e.message || "Could not load How To Test." });
      }
    })();
    return () => {
      alive = false;
    };
  }, [testKeysParam]);

  const toggleTest = (key) =>
    setOpenTests((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allTestsOpen = tests.length > 0 && tests.every((x) => openTests.has(x.key));
  /* A card with a live run stays open regardless — collapsing it would hide
     the progress of something that is still going. */
  const runningXrayKeys = new Set(
    cases.filter((c) => running.has(c.id)).map((c) => c.xrayKey),
  );

  // Load every authored test case across this story's Xray tests, tagged with
  // its Xray key so we can group the rows under their test.
  useEffect(() => {
    let cancelled = false;
    const pairs = [];
    for (const x of xrayTests || []) for (const c of x.testcases || []) pairs.push([x.key, c.testcasekey]);
    if (pairs.length === 0) {
      setCases([]);
      return undefined;
    }
    setCasesLoading(true);
    (async () => {
      const loaded = await Promise.all(
        pairs.map(async ([xrayKey, key]) => {
          try {
            const r = await fetch(`${API_BASE_URL}testcases/${encodeURIComponent(key)}`);
            const b = await r.json().catch(() => ({}));
            if (b?.data) return { ...fromApi(b.data), xrayKey };
          } catch {
            /* ignore */
          }
          return null;
        })
      );
      if (!cancelled) {
        setCases(loaded.filter(Boolean));
        setCasesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.key, xrayTests]);

  // Close any live run sockets on unmount.
  useEffect(
    () => () => {
      for (const ws of Object.values(wsRefs.current)) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
    },
    []
  );

  const patchCase = (id, patch) =>
    setCases((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const patchStep = (id, idx, patch) =>
    setCases((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, steps: c.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }
          : c
      )
    );


  const handleRunEvent = (id, evt) => {
    if (!evt || !evt.type) return;
    switch (evt.type) {
      case "step_start":
        patchStep(id, evt.index - 1, {
          status: "running",
          action: evt.action,
          selector: evt.selector,
          value: evt.value,
          error: null,
        });
        break;
      case "prompt_input":
        patchStep(id, evt.index - 1, { status: "running" });
        setPendingPrompts((p) => ({
          ...p,
          [id]: {
            index: evt.index,
            param_name: evt.param_name,
            field_label: evt.field_label,
            kind: evt.kind,
            masked: !!evt.masked,
            action: evt.action,
            selector: evt.selector,
          },
        }));
        break;
      case "prompt_credentials":
        setPendingCredentials((p) => ({ ...p, [id]: true }));
        break;
      case "step_result":
        patchStep(id, evt.index - 1, {
          status: evt.status === "pass" ? "passed" : "failed",
          screenshot: evt.screenshot || null,
          error: evt.error || null,
        });
        break;
      case "errors_detected":
        runAppRejected.current[id] = true;
        patchStep(id, evt.step_index - 1, {
          detectedErrors: Array.isArray(evt.errors) ? evt.errors : [],
        });
        break;
      case "done":
        patchCase(id, {
          status: evt.status === "cancelled" ? "cancelled" : evt.status === "passed" ? "passed" : "failed",
        });
        setPendingPrompts((p) => {
          const n = { ...p };
          delete n[id];
          return n;
        });
        setPendingCredentials((p) => {
          const n = { ...p };
          delete n[id];
          return n;
        });
        break;
      case "error":
        patchCase(id, { status: "failed" });
        setPendingPrompts((p) => {
          const n = { ...p };
          delete n[id];
          return n;
        });
        setPendingCredentials((p) => {
          const n = { ...p };
          delete n[id];
          return n;
        });
        break;
      default:
        break;
    }
  };

  const runCase = (id, datasourcekey = null) =>
    new Promise((resolve) => {
      setCases((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...c,
                status: "running",
                steps: c.steps.map((s) => ({ ...s, status: "pending", screenshot: null, error: null })),
              }
            : c
        )
      );
      setExpanded((prev) => new Set(prev).add(id));
      setRunning((prev) => new Set(prev).add(id));
      runAppRejected.current[id] = false;
      (async () => {
        let runId = null;
        try {
          const res = await fetch(`${API_BASE_URL}testcases/${id}/run`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            /* A trial run: local, nothing written to Jira, never evidence. */
            body: JSON.stringify({ userkey: userkey || null, datasourcekey: datasourcekey || null, source: "requirement_board" }),
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok || !body?.data?.run_id) throw new Error(body?.detail || `HTTP ${res.status}`);
          runId = body.data.run_id;
        } catch {
          patchCase(id, { status: "failed" });
          setRunning((prev) => {
            const n = new Set(prev);
            n.delete(id);
            return n;
          });
          return resolve();
        }
        const ws = new WebSocket(apiWsUrl(`/api/ws/run/${runId}`));
        wsRefs.current[id] = ws;
        ws.onmessage = (e) => {
          try {
            handleRunEvent(id, JSON.parse(e.data));
          } catch {
            /* ignore */
          }
        };
        ws.onclose = () => {
          delete wsRefs.current[id];
          setRunning((prev) => {
            const n = new Set(prev);
            n.delete(id);
            return n;
          });
          resolve();
        };
      })();
    });

  const stopCase = (id) => {
    const ws = wsRefs.current[id];
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "cancel" }));
      } catch {
        /* ignore */
      }
    }
    setCases((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              status: "cancelled",
              steps: c.steps.map((s) => (s.status === "running" ? { ...s, status: "pending" } : s)),
            }
          : c
      )
    );
  };

  const submitPromptInput = (id, index, value) => {
    const ws = wsRefs.current[id];
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "input_value", index, value }));
      } catch {
        /* ignore */
      }
    }
    setPendingPrompts((p) => {
      const cur = p[id];
      if (!cur || cur.index !== index) return p;
      const n = { ...p };
      delete n[id];
      return n;
    });
  };

  const submitCredentials = (id, username, password) => {
    const ws = wsRefs.current[id];
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "credentials_value", username, password }));
      } catch {
        /* ignore */
      }
    }
    setPendingCredentials((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });
  };

  // const saveEdits = async (id, name, steps) => {
  //   try {
  //     const res = await fetch(`${API_BASE_URL}testcases/${encodeURIComponent(id)}`, {
  //       method: "PUT",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         name,
  //         steps: (steps || []).map((s) => ({
  //           action: s.action,
  //           selector: s.selector,
  //           value: s.value,
  //           ...(s.source_step_no != null ? { source_step_no: s.source_step_no } : {}),
  //           ...(s.is_dynamic ? { is_dynamic: true, param_name: s.param_name } : {}),
  //         })),
  //       }),
  //     });
  //     const b = await res.json().catch(() => ({}));
  //     if (res.ok && b?.data) {
  //       const mapped = fromApi(b.data);
  //       patchCase(id, { name: mapped.name, steps: mapped.steps });
  //     }
  //   } catch {
  //     /* ignore */
  //   }
  // };

  // const deleteCase = async (id) => {
  //   if (!window.confirm("Delete this test case? This can't be undone.")) return;
  //   try {
  //     const res = await fetch(`${API_BASE_URL}testcases/${encodeURIComponent(id)}`, { method: "DELETE" });
  //     if (res.ok) setCases((prev) => prev.filter((c) => c.id !== id));
  //   } catch {
  //     /* ignore */
  //   }
  // };

  const casesForXray = (xrayKey) => cases.filter((c) => c.xrayKey === xrayKey);

  // Full-page run view for one case (same component board detail uses).
  if (viewCaseId) {
    const vc = cases.find((c) => c.id === viewCaseId);
    if (vc) {
      return (
        <RunView
          testCase={vc}
          frameUrl={null}
          isRunning={running.has(viewCaseId)}
          isPaused={false}
          onBack={() => setViewCaseId(null)}
          onRun={runCase}
          onStop={(id) => {
            stopCase(id);
            setViewCaseId(null);
          }}
          onPause={() => {}}
          onResume={() => {}}
          onHistory={() => {}}
          pendingPrompt={pendingPrompts[viewCaseId] || null}
          onSubmitPrompt={(value) => submitPromptInput(viewCaseId, pendingPrompts[viewCaseId]?.index, value)}
          pendingCredentials={!!pendingCredentials[viewCaseId]}
          onSubmitCredentials={(u, p) => submitCredentials(viewCaseId, u, p)}
        />
      );
    }
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col">
      {/* Header */}
      <div className="mb-4 flex-none">
        <div className="flex flex-wrap items-end justify-between gap-3">
          {/* Story key + name only. Leaving the screen is the static Cancel in
              the footer; the Build / Create-execution actions live on the
              board, not here. */}
          <div className="flex min-w-0 items-center gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {/* Jira story icon instead of a "Story" text chip. */}
                <Icons.JiraStory aria-label="Story" role="img" />
                <span className="text-sm font-semibold text-blue-600">{story.key}</span>
                <JiraStatusBadge status={story.status} category={story.status_category} title={issueStatusNote("Story", story.key, story.status)} />
                <PriorityBadge priority={story.priority} />
              </div>
              <h1 className="mt-0.5 truncate text-xl font-bold tracking-tight text-slate-900">
                {story.summary || "(no summary)"}
              </h1>
            </div>
          </div>
          {tests.length > 1 && (
            <button
              type="button"
              onClick={() =>
                setOpenTests(allTestsOpen ? new Set() : new Set(tests.map((x) => x.key)))
              }
              className="flex-none rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
            >
              {allTestsOpen ? "Collapse all" : "Expand all"}
            </button>
          )}
        </div>
      </div>

      {/* One white card, same as the Build Playwright scripts screen: only the
          body inside it scrolls, and the Cancel footer is its bottom bar. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-card shadow-sm">
      {/* Scroll body */}
      <div className="cq-scroll min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
        {/* The story's tests — each Xray test with its Playwright script(s).
            No section header: the story above already says what this is. */}
        <section>
          {coverageLoading || casesLoading ? (
            /* Same guide popup as the board, not the old inline spinner. */
            <Loader.Overlay
              variant="guide"
              label="Loading story…"
              sublabel={`Fetching tests and Artifact for ${story.key}`}
              delay={0}
            />
          ) : coverageError ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Couldn't read the tests for {story.key}: {coverageError}
            </div>
          ) : tests.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-card px-6 py-10 text-center">
              <p className="text-sm text-slate-500">No tests linked to this story yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tests.map((x) => {
                const rows = casesForXray(x.key);
                const open = openTests.has(x.key) || runningXrayKeys.has(x.key);
                // Scenario lines only — Manual steps are not shown.
                const stepCount = scenarioCount(x.steps);
                return (
                  <div key={x.key} className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
                    <button
                      type="button"
                      onClick={() => toggleTest(x.key)}
                      aria-expanded={open}
                      title={open ? "Collapse this test" : "Expand this test"}
                      className={[
                        "flex w-full flex-wrap items-center gap-2 bg-blue-50/40 px-4 py-2.5 text-left transition hover:bg-blue-50",
                        open ? "border-b border-slate-100" : "",
                      ].join(" ")}
                    >
                      <Icons.ChevronRight
                        className={`h-4 w-4 flex-none text-slate-400 transition-transform ${open ? "rotate-90" : ""}`}
                      />
                      <span
                        className="flex-none text-sm font-medium text-blue-600"
                        title={`Test ${x.key}`}
                      >
                        {x.key}
                      </span>
                      <span
                        className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800"
                        title={x.summary || ""}
                      >
                        {plainTitle(x.summary, story.key) || "(no summary)"}
                      </span>
                      {/* Closed, these counts are the only summary of what is
                          inside, so they belong on the header rather than in
                          the body they describe. */}
                      {stepCount > 0 && (
                        <span className="flex-none text-[10px] font-medium text-slate-400">
                          {stepCount} step{stepCount === 1 ? "" : "s"}
                        </span>
                      )}
                      {/* The one run status for a single script — whether it
                          has run, and how it went. Several scripts each show
                          their own above their table instead. */}
                      {rows.length === 1 && <ScriptAuthor tc={rows[0]} />}
                      {rows.length === 1 && <StatusBadge status={rows[0].status} />}
                      {x.automated ? (
                        <span className="inline-flex flex-none items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                          <Icons.Check className="h-3.5 w-3.5" /> Automated
                        </span>
                      ) : (
                        <span className="flex-none rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                          Not automated
                        </span>
                      )}
                    </button>
                    {open && (
                      /* Two sections, both from Jira: the test's "How To
                         Test" field first, then its steps (the Cucumber
                         scenario, or "No Cucumber scenario…"). */
                      <div>
                        <div>
                          <p className="border-b border-slate-200 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-800">
                            How To Test
                          </p>
                          <div className="px-4 py-3 text-[13px] leading-relaxed text-slate-700">
                            {howToTest.loading ? (
                              <span className="flex items-center gap-2 text-xs text-slate-400">
                                <Icons.Spinner className="h-3.5 w-3.5 animate-spin" /> Loading How To Test…
                              </span>
                            ) : howToTest.error ? (
                              <span className="text-xs text-rose-700">Couldn't read How To Test: {howToTest.error}</span>
                            ) : !howToTest.found ? (
                              <span className="text-xs text-slate-400">This Jira has no "How To Test" field.</span>
                            ) : howToTest.items[x.key] ? (
                              <ChatMarkdown text={howToTest.items[x.key]} />
                            ) : (
                              <span className="text-xs text-slate-400">How To Test is empty in Jira.</span>
                            )}
                          </div>
                        </div>
                        <div className="border-t border-slate-200">
                          <XrayTestSteps steps={x.steps} />
                        </div>
                        {rows.length > 0 && (
                          <div className="space-y-1.5 border-t border-slate-100 px-4 py-3">
                            {trialWarning(x.key, x.status) && (
                              <p className="flex items-start gap-1.5 text-[12px] text-amber-800">
                                <Icons.Alert className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden="true" />
                                {trialWarning(x.key, x.status)}
                              </p>
                            )}
                            {rows.map((r) => (
                              <div key={r.id} className="flex flex-wrap items-center gap-2 text-[12px] text-slate-600">
                                <Icons.Script className="h-3.5 w-3.5 flex-none text-slate-400" aria-hidden="true" />
                                <span className="min-w-0 flex-1 truncate font-medium text-slate-700" title={r.name}>{r.name}</span>
                                <span className="text-[11px] text-slate-400">{TRIAL_LABEL}. {EVIDENCE_POINTER}</span>
                                <ActivityButton title={`${x.key} — ${r.name}`} scope={{ testcasekey: r.id }} testKey={x.key} />
                              </div>
                            ))}
                          </div>
                        )}
                        {rows.length === 0 && (
                          /* The offer sits next to the gap it fills. */
                          <div className="border-t border-slate-100 px-4 py-3 text-center">
                            <p className="text-xs font-semibold text-slate-700">
                              No artifact automates this test yet.
                            </p>
                            <button
                              type="button"
                              onClick={() => onCreatePlaywright?.()}
                              className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-brand-700"
                            >
                              <Icons.Plus className="h-4 w-4" /> Create Artifact
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Static footer — the only way out of this screen. Stays pinned while
          the test list above scrolls. */}
      <div className="flex flex-none justify-end border-t border-slate-200 bg-slate-50/70 px-6 py-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
        >
          Cancel
        </button>
      </div>
      </div>

      {/* Dynamic-value / credentials prompts for runs started from a row */}
      {Object.entries(pendingPrompts).map(([id, prompt]) => (
        <DynamicPromptModal
          key={`prompt-${id}`}
          prompt={prompt}
          onSubmit={(value) => submitPromptInput(id, prompt.index, value)}
        />
      ))}
      {Object.keys(pendingCredentials).map((id) => (
        <CredentialsPromptModal key={`cred-${id}`} onSubmit={(u, p) => submitCredentials(id, u, p)} />
      ))}

      {/* Owned by the board (it holds the story + coverage), rendered here too
          so Create execution works from the full-step view as well. */}
      {execDialog}
    </div>
  );
}

/* ================================================================== */
/*  Requirement Board                                                  */
/* ================================================================== */
export default function RequirementBoardView({
  userkey,
  onCreateEpic,
  onCreateStory,
  onCreateXray,
  onCreateExecution,
  onCreatePlaywright,
  /* "Save & Execute": open the Test Runs page on { epicKey, storyKey, testKey }. */
  onOpenTestRuns,
  /* Bumped by the shell when an authoring flow we launched hands control back.
     This component stays mounted across navigation and its Xray coverage is
     cached, so it needs telling that the world changed underneath it. */
  refreshSeq = 0,
  /* Set when a Playwright generation was saved from its tab. Overrides the
     reset-on-return below: the board re-opens on the same epic / story with
     the generated tests expanded, and the new scripts waiting for a decision.
     Shape: { seq, epicKey, storyKey, xrayTestKeys, inboxKeys, cases }. */
  returnTo = null,
  /* True when this tab is the active nav item. Used to reset selections
     whenever the user navigates away and then comes back. */
  visible = false,
}) {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");

  const [epics, setEpics] = useState([]);
  const [epicsLoading, setEpicsLoading] = useState(false);
  const [selectedEpic, setSelectedEpic] = useState("");

  const [stories, setStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [selectedStory, setSelectedStory] = useState("");

  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  /* Xray tests linked directly to the selected epic — tests that belong to the
     epic rather than to any story card under it. Same shape as `coverage`. */
  const [epicCoverage, setEpicCoverage] = useState(null);
  const [epicCoverageLoading, setEpicCoverageLoading] = useState(false);
  const [epicTestsOpen, setEpicTestsOpen] = useState(false);

  /* Project-wide: Xray tests attached to no epic and no story at all. */
  const [orphans, setOrphans] = useState(null);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphansOpen, setOrphansOpen] = useState(false);

  // Jira key -> { ticketkey, execution_count } for locally anchored tickets.
  const [linkMap, setLinkMap] = useState({});
  const [executions, setExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);

  // Story opened into its own detail screen (via the card's "Enter" button).
  const [storyDetailOpen, setStoryDetailOpen] = useState(false);

  /* true = ascending (default), false = descending */
  const [epicSortAsc, setEpicSortAsc] = useState(true);
  const [storySortAsc, setStorySortAsc] = useState(true);

  /* True from the moment the board starts loading (fresh mount OR project
     change) until epics, stories, orphans and coverage have all settled.
     Initialised to true so the guide-variant popup already covers the FIRST
     paint — otherwise the status check + project list fetch happens before
     the overlay even has a reason to go up, and the column skeletons would
     flash first. */
  const [projectLoading, setProjectLoading] = useState(true);
  /* What the overlay went up for — { kind: "project" | "epic" | "story" |
     "return", key } — fixed when it is raised. Deriving the text from the live
     loading flags fell back to "Loading project…" during the settle delay,
     after the story's / epic's own load had already finished. */
  const [loadingFor, setLoadingFor] = useState({ kind: "project", key: "" });
  /* A project load, or a return to the board, outranks the epic / story
     loads it sets off; otherwise the latest trigger wins. Cleared when the
     overlay settles. */
  const raiseOverlay = useCallback((kind, key = "") => {
    setProjectLoading(true);
    setLoadingFor((prev) =>
      prev?.kind === "project" || prev?.kind === "return" ? prev : { kind, key },
    );
  }, []);
  /* Loading flag for the one async that didn't track its own: the Jira
     projects fetch. Watched by the settle effect so the overlay doesn't flash
     off in the gap between statusLoading finishing and loadEpics starting. */
  const [projectsLoading, setProjectsLoading] = useState(false);

  /* Bumped by Refresh. The executions fetch keys off the ticket anchor map, so
     without this it only re-runs when that map actually changes — a refresh
     that returns an identical set of anchors would leave the executions list
     untouched. */
  const [refreshNonce, setRefreshNonce] = useState(0);
  /* True only while a user-pressed Refresh is in flight. Deliberately separate
     from the per-list loading flags, which fire on ordinary navigation too. */
  const [refreshing, setRefreshing] = useState(false);

  /* Create-execution dialog, pre-scoped to the story already selected here.
     Shape: { story, name, description, selected, linkTickets, scopeTicketKeys }
     or { story, blocked: "…" } when there is nothing runnable yet. Creating an
     execution used to jump to the Executions tab and start from an empty form,
     throwing away the epic, story and tests the user had just picked. */
  const [execFlow, setExecFlow] = useState(null);
  const [execStarting, setExecStarting] = useState(false);

  // Xray tests expanded to their steps in the right-hand panel.
  const [openXray, setOpenXray] = useState(() => new Set());
  const toggleXray = (key) =>
    setOpenXray((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const [error, setError] = useState("");
  /* A story picked from the search that has no epic: the Story column only
     loads for a selected epic, so it is pinned there instead. */
  const [pinnedStory, setPinnedStory] = useState(null);
  // Per-column status filter for the Story Cards column (All by default).
  const [storyStatusFilter, setStoryStatusFilter] = useState("");

  // --- Jira connection status ------------------------------------------------
  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}jira/status`);
      const body = await res.json().catch(() => ({}));
      setStatus(body?.data || { connected: false });
    } catch {
      setStatus({ connected: false, email: "Network error while contacting the server." });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  // --- Projects --------------------------------------------------------------
  const loadProjects = useCallback(async () => {
    setError("");
    setProjectsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}jira/projects`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.detail || "Failed to load Jira projects.");
        setProjects([]);
        return;
      }
      const list = Array.isArray(body?.data) ? body.data : [];
      setProjects(list);
      /* Keep the current pick when it is still a real project — that is how a
         restored selection survives — otherwise open on PSR (defaultProjectOf). */
      if (list.length) {
        setSelectedProject((cur) =>
          cur && list.some((p) => p.key === cur) ? cur : defaultProjectOf(list)
        );
      }
    } catch {
      setError("Network error while loading Jira projects.");
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  // --- Epics for the selected project (Column 1) -----------------------------
  const loadEpics = useCallback(async (projectKey, search = "") => {
    if (!projectKey) {
      setEpics([]);
      return;
    }
    setEpicsLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}jira/epics?project=${encodeURIComponent(projectKey)}` +
          (search ? `&search=${encodeURIComponent(search)}` : "")
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.detail || "Failed to load epics for this project.");
        setEpics([]);
        return;
      }
      setEpics(Array.isArray(body?.data) ? body.data : []);
    } catch {
      setError("Network error while loading epics.");
      setEpics([]);
    } finally {
      setEpicsLoading(false);
    }
  }, []);

  // --- Story cards for the project / selected epic (Column 2) ----------------
  // Fetches Story-type issues only (Tests and Epics must not appear as story
  // cards). When an epic is selected we filter server-side (`epic=<key>`), so
  // the column shows THAT epic's complete story set instead of hoping the
  // stories happen to fall within the newest 100 issues project-wide.
  const loadStories = useCallback(async (projectKey, epicKey, search = "") => {
    if (!projectKey) {
      setStories([]);
      return;
    }
    setStoriesLoading(true);
    setError("");
    try {
      let url =
        `${API_BASE_URL}jira/tickets?project=${encodeURIComponent(projectKey)}` +
        `&max_results=100&issue_type=Story`;
      if (epicKey) url += `&epic=${encodeURIComponent(epicKey)}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.detail || "Failed to load stories.");
        setStories([]);
        return;
      }
      setStories(Array.isArray(body?.data?.issues) ? body.data.issues : []);
    } catch {
      setError("Network error while loading stories.");
      setStories([]);
    } finally {
      setStoriesLoading(false);
    }
  }, []);

  // --- Xray + Playwright tests linked to the selected story (Column 3) -------
  /* `refresh` bypasses the API's short-lived Xray discovery cache. Selecting a
     story card should hit that cache — it is what makes re-selection instant —
     but pressing Refresh must not, or the button quietly returns the same
     60-second-old answer. */
  const loadCoverage = useCallback(async (storyKey, { refresh = false } = {}) => {
    if (!storyKey) {
      setCoverage(null);
      return;
    }
    setCoverageLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}coverage/tickets?keys=${encodeURIComponent(storyKey)}${
          refresh ? "&refresh=true" : ""
        }`
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCoverage(null);
        return;
      }
      const ticket = (body?.data?.tickets || [])[0] || null;
      setCoverage(ticket);
    } catch {
      setCoverage(null);
    } finally {
      setCoverageLoading(false);
    }
  }, []);

  /* --- Xray tests hanging off the EPIC itself --------------------------------

     Xray tests are normally linked to a story card, but nothing in Jira stops
     someone linking them straight to the epic. Those were invisible here — the
     board only ever asked about the selected story — so they looked like work
     that had never been designed. `/coverage/tickets` takes any issue key, so
     the epic can be asked the same question a story is. */
  const loadEpicCoverage = useCallback(async (epicKey) => {
    if (!epicKey) {
      setEpicCoverage(null);
      return;
    }
    setEpicCoverageLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}coverage/epic-xray?epic=${encodeURIComponent(epicKey)}`
      );
      const body = await res.json().catch(() => ({}));
      setEpicCoverage(res.ok ? body?.data || null : null);
    } catch {
      setEpicCoverage(null);
    } finally {
      setEpicCoverageLoading(false);
    }
  }, []);

  /* --- Xray tests attached to nothing at all ---------------------------------

     The board can only reach a test by walking an epic or a story card, so a
     test linked to neither is invisible here however hard you look. It is also
     the most wasteful state to be in: the design work is done and nothing is
     using it. Asked once per project, from the Test's side of the link.
     Not called while the orphan banner below is commented out — the landing
     page loads epics only. */
  // eslint-disable-next-line no-unused-vars
  const loadOrphans = useCallback(async (projectKey) => {
    if (!projectKey) {
      setOrphans(null);
      return;
    }
    setOrphansLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}coverage/unlinked-xray?project=${encodeURIComponent(projectKey)}`
      );
      const body = await res.json().catch(() => ({}));
      setOrphans(res.ok ? body?.data || null : null);
    } catch {
      setOrphans(null);
    } finally {
      setOrphansLoading(false);
    }
  }, []);

  // --- Local ticket anchors (so we know which stories have executions) -------
  const loadLinks = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}ticket-links`);
      const body = await res.json().catch(() => ({}));
      const list = Array.isArray(body?.data) ? body.data : [];
      const map = {};
      for (const a of list) {
        map[a.ticket_key] = {
          ticketkey: a.ticketkey,
          execution_count: a.execution_count || 0,
        };
      }
      setLinkMap(map);
    } catch {
      /* leave existing map */
    }
  }, []);

  /* Drive the overlay down ONLY when every load that could have triggered it
     has finished — status + projects (boot chain) + epics + stories + orphans
     (project / epic click) + coverage (story click). The reload effects fire a
     tick after projectLoading flips on, so a short settle delay keeps the
     overlay from flashing off in that gap. Watching statusLoading and
     projectsLoading here is what prevents the overlay from closing during the
     fresh-mount boot chain before any per-column flag has had a chance to turn
     true. */
  useEffect(() => {
    if (!projectLoading) return undefined;
    if (
      statusLoading ||
      projectsLoading ||
      epicsLoading ||
      storiesLoading ||
      orphansLoading ||
      coverageLoading
    )
      return undefined;
    const t = setTimeout(() => {
      setProjectLoading(false);
      setLoadingFor(null);
    }, 120);
    return () => clearTimeout(t);
  }, [
    projectLoading,
    statusLoading,
    projectsLoading,
    epicsLoading,
    storiesLoading,
    orphansLoading,
    coverageLoading,
  ]);

  // --- Effects: load project data; epic is a client-side filter --------------
  useEffect(() => {
    loadStatus();
    loadLinks();
  }, [loadStatus, loadLinks]);

  useEffect(() => {
    if (status?.connected) loadProjects();
  }, [status?.connected, loadProjects]);

  // Project change: reset selections and (re)load epics.
  // Flip projectLoading on here (not only in the dropdown onChange) so the
  // guide-variant popup also covers the INITIAL board load — when the first
  // project auto-selects, this effect fires and the overlay goes up BEFORE
  // the column skeletons would otherwise be the only thing on screen.
  useEffect(() => {
    setSelectedEpic("");
    setSelectedStory("");
    setCoverage(null);
    setStoryDetailOpen(false);
    setOrphansOpen(false);
    if (selectedProject) {
      setProjectLoading(true);
      setLoadingFor({ kind: "project", key: selectedProject });
      loadEpics(selectedProject);
    } else {
      setEpics([]);
      setStories([]);
      setOrphans(null);
    }
  }, [selectedProject, loadEpics]);

  // Load the Story column only once an epic is chosen — the landing page
  // shows epics alone. With an epic it shows exactly that epic's stories
  // (complete, not limited to whatever landed in the newest 100 issues
  // project-wide).
  // Flip projectLoading on so the guide popup also covers an epic click
  // (stories-only reload). The settle effect clears it when storiesLoading
  // goes false.
  useEffect(() => {
    if (selectedProject && selectedEpic) {
      raiseOverlay("epic", selectedEpic);
      loadStories(selectedProject, selectedEpic);
    } else {
      setStories([]);
    }
  }, [selectedProject, selectedEpic, loadStories, raiseOverlay]);

  /* Switching epics must drop the story that was selected under the OLD epic.
     Without this the Story column reloaded for the new epic while the right
     panel kept showing the previous story's Xray tests and executions — a
     story that is not even in the list any more. Skipped on the first run so a
     restored session keeps the story it resumed with. */
  const epicForStory = useRef(selectedEpic);
  useEffect(() => {
    if (epicForStory.current === selectedEpic) return;
    epicForStory.current = selectedEpic;
    setSelectedStory("");
    setCoverage(null);
    setExecutions([]);
    setStoryDetailOpen(false);
    setOpenXray(new Set());
    setPinnedStory(null);
  }, [selectedEpic]);

  /* Selecting a story selects its parent epic too — the board reads
     epic → story → tests, so a story shown with no epic above it is only half
     the context. A search that matches a story but not its epic (searching
     "1249" matches PSR-1249 only) left the Epics column empty.

     `epicForStory` is updated in the same tick so the clear-on-epic-change
     effect above does not read this as a genuine epic switch and immediately
     throw the story away. */
  const selectStory = useCallback(
    (story) => {
      if (!story?.key) return;
      const epicKey = (story.epic_key || "").trim();
      if (epicKey && epicKey !== selectedEpic) {
        epicForStory.current = epicKey;
        setSelectedEpic(epicKey);
      }
      setSelectedStory(story.key);
    },
    [selectedEpic],
  );

  /* A result picked from the board search. An epic selects that epic; a
     story selects its epic and itself; a test selects its story (and epic)
     and opens that test in the Tests column. A story with no epic is pinned
     to the Story column, which otherwise loads only for a selected epic. */
  const pickFromSearch = useCallback(
    (group, item) => {
      if (group === "epics") {
        setSelectedEpic(item.key);
        return;
      }
      const story = group === "stories" ? item : item.story;
      if (!story?.key) {
        // A test on an epic directly: the epic is as far as the board goes.
        if (item.epic_key) setSelectedEpic(item.epic_key);
        return;
      }
      if (story.epic_key) {
        setPinnedStory(null);
        selectStory(story);
      } else {
        epicForStory.current = "";
        setSelectedEpic("");
        setPinnedStory(story);
        setSelectedStory(story.key);
      }
      if (group === "tests") setOpenXray(new Set([item.key]));
    },
    [selectStory],
  );

  /* The parent epic may not be in the loaded epics list — a project-wide
     search returns only what matched. Pin it so the column can still show it
     (and show it as selected) instead of rendering an empty rail. */
  const [pinnedEpic, setPinnedEpic] = useState(null);
  useEffect(() => {
    if (!selectedEpic || !selectedProject) {
      setPinnedEpic(null);
      return;
    }
    if (epics.some((e) => e.key === selectedEpic)) {
      setPinnedEpic(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}jira/epics?project=${encodeURIComponent(selectedProject)}` +
            `&search=${encodeURIComponent(selectedEpic)}`
        );
        const body = await res.json().catch(() => ({}));
        const hit = (Array.isArray(body?.data) ? body.data : []).find(
          (e) => e.key === selectedEpic
        );
        if (!cancelled) setPinnedEpic(hit || { key: selectedEpic, summary: "", status: "" });
      } catch {
        if (!cancelled) setPinnedEpic({ key: selectedEpic, summary: "", status: "" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEpic, selectedProject, epics]);

  /* Reset all selections whenever the user navigates back to this board.
     prevVisible tracks the last known visibility so we only reset on the
     false→true transition, not on every render. */
  const prevVisible = useRef(false);
  useEffect(() => {
    if (visible && !prevVisible.current) {
      setSelectedEpic("");
      setSelectedStory("");
      setStoryStatusFilter("");
      setStoryDetailOpen(false);
      setPinnedStory(null);
      setCoverage(null);
    }
    prevVisible.current = visible;
  }, [visible]);

  /* Back from Create Artifact, where the generated Artifacts were already
     checked and saved: undo the reset above and land where the user left —
     same epic, same story, the generated tests open and shown as Automated.
     Declared after the reset effect so it runs after it in the same commit. */
  useEffect(() => {
    if (!returnTo?.storyKey) return;
    /* Restoring the epic + story reloads both; one "back to the board"
       message covers them rather than the per-load wording. */
    setProjectLoading(true);
    setLoadingFor({ kind: "return", key: "" });
    const epicKey = (returnTo.epicKey || "").trim();
    if (epicKey) {
      epicForStory.current = epicKey;
      setSelectedEpic(epicKey);
    }
    setSelectedStory(returnTo.storyKey);
    /* Coming back to the SAME story the board was on: the reset above set the
       story to "" and this sets it straight back in the same commit, so React
       sees no change and the story → coverage effect never re-runs — but the
       reset's setCoverage(null) did apply, leaving the Tests column empty.
       Reload it here. (A different story is loaded by that effect as usual.) */
    if (returnTo.storyKey === selectedStory) {
      raiseOverlay("story", returnTo.storyKey);
      loadCoverage(returnTo.storyKey);
    }
    setOpenXray(new Set(returnTo.xrayTestKeys || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnTo?.seq]);

  /* The remembered story may no longer be in this project's list (deleted, or
     pushed past the page size). Fall back to the board rather than sitting on
     a detail screen for a story we cannot show. */
  useEffect(() => {
    if (!storyDetailOpen || !selectedStory) return;
    if (storiesLoading || stories.length === 0) return;
    if (!stories.some((s) => s.key === selectedStory)) setStoryDetailOpen(false);
  }, [storyDetailOpen, selectedStory, stories, storiesLoading]);

  // Story click → coverage reload. Also flip the guide popup on so clicking a
  // story shows the same overlay instead of the inline "Discovering Xray
  // tests…" message in the right panel. The settle effect watches
  // coverageLoading too and clears projectLoading once it goes false.
  useEffect(() => {
    if (selectedStory) {
      raiseOverlay("story", selectedStory);
      loadCoverage(selectedStory);
    } else {
      setCoverage(null);
    }
  }, [selectedStory, loadCoverage, raiseOverlay]);

  useEffect(() => {
    setEpicTestsOpen(false);
    if (selectedEpic) loadEpicCoverage(selectedEpic);
    else setEpicCoverage(null);
  }, [selectedEpic, loadEpicCoverage]);

  /* Came back from authoring. Re-read the story's Xray coverage with the
     discovery cache bypassed — the whole point of returning here is to see the
     scripts that were just created. */
  useEffect(() => {
    if (!refreshSeq) return;
    if (selectedStory) loadCoverage(selectedStory, { refresh: true });
    if (selectedEpic) loadEpicCoverage(selectedEpic);
    loadLinks();
    setRefreshNonce((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSeq]);

  // Executions created for the selected story card (via its local anchor).
  useEffect(() => {
    if (!selectedStory) {
      setExecutions([]);
      return undefined;
    }
    const anchor = linkMap[selectedStory];
    if (!anchor?.ticketkey) {
      setExecutions([]);
      return undefined;
    }
    let cancelled = false;
    setExecutionsLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}ticket-links/${encodeURIComponent(anchor.ticketkey)}`
        );
        const body = await res.json().catch(() => ({}));
        if (!cancelled) {
          setExecutions(
            Array.isArray(body?.data?.executions) ? body.data.executions : []
          );
        }
      } catch {
        if (!cancelled) setExecutions([]);
      } finally {
        if (!cancelled) setExecutionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedStory, linkMap, refreshNonce]);

  const selectedEpicObj = useMemo(
    () => epics.find((e) => e.key === selectedEpic) || null,
    [epics, selectedEpic]
  );
  const selectedStoryObj = useMemo(
    () =>
      stories.find((s) => s.key === selectedStory) ||
      (pinnedStory?.key === selectedStory ? pinnedStory : null),
    [stories, selectedStory, pinnedStory]
  );

  /* The board reads top-down — epics, story cards, that story's Xray tests,
     then the executions run against them — so it should fill in that order.
     Executions resolve first (local DB vs a round trip to Jira + Xray Cloud),
     so they are held until the Xray section has settled. Nothing is serialised:
     both requests still go out at the same moment. */


  /* Open the execution dialog for a story, carrying everything already on
     screen into it: the story as the Jira scope, and every Playwright case
     behind its Xray tests pre-selected. `coverage` already holds those, so no
     extra round trip is needed to populate the picker.

     The one thing it does fetch is the local ticket anchor, because an
     execution is linked to the story through that row and it may not exist
     yet. */
  const startExecution = useCallback(
    async (story) => {
      if (!story?.key || execStarting) return;
      setExecStarting(true);
      try {
        const tests = coverage?.ticket_key === story.key ? coverage?.tests || [] : [];
        const selected = {};
        for (const x of tests) {
          for (const c of x.testcases || []) {
            selected[c.testcasekey] = {
              name: c.name,
              board_id: c.board_id,
              boardName: c.board_name,
              priority: c.priority,
              // Kept so the execution is linked to the story it came from.
              ticket_key: story.key,
            };
          }
        }

        if (Object.keys(selected).length === 0) {
          setExecFlow({
            story,
            blocked:
              tests.length === 0
                ? "This story has no tests yet. Build the tests first, then the Artifact scripts that automate them."
                : "This story's tests have no Artifact scripts yet. An execution runs the scripts, so build those first.",
          });
          return;
        }

        // The execution links to the story through its local anchor row.
        let ticketkey = linkMap[story.key]?.ticketkey || null;
        if (!ticketkey) {
          try {
            const res = await fetch(`${API_BASE_URL}ticket-links`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                ticket_key: story.key,
                project_key: selectedProject || "",
                epic_key: story.epic_key || "",
                summary: story.summary || "",
                status: story.status || "",
                issue_type: story.issue_type || "",
                priority: story.priority || "",
                url: story.url || "",
              }),
            });
            const body = await res.json().catch(() => ({}));
            if (res.ok && body?.data?.ticketkey) ticketkey = body.data.ticketkey;
          } catch {
            /* The wizard still sends link_jira_keys, so the API can anchor it. */
          }
        }

        setExecFlow({
          story,
          name: `${story.key} — execution`,
          description: `Automated tests for ${story.key}: ${story.summary || ""}`.trim(),
          selected,
          linkTickets: ticketkey
            ? [{ ticketkey, ticket_key: story.key, summary: story.summary || "" }]
            : [],
          scopeTicketKeys: [story.key],
        });
      } finally {
        setExecStarting(false);
      }
    },
    [coverage, linkMap, selectedProject, execStarting],
  );





  /* Searching is done by BoardSearch against the API, never by filtering the
     columns locally: only one page of issues is ever loaded, so a local filter
     made searching "1249" look like PSR-1249 did not exist. */
  const filteredEpics = useMemo(() => {
    const list = pinnedEpic ? [pinnedEpic, ...epics] : epics;
    return [...list].sort((a, b) => {
      const n = (k) => parseInt((k || "").split("-").pop(), 10) || 0;
      return epicSortAsc ? n(a.key) - n(b.key) : n(b.key) - n(a.key);
    });
  }, [epics, pinnedEpic, epicSortAsc]);

  const filteredStories = useMemo(() => {
    const all =
      pinnedStory && !stories.some((s) => s.key === pinnedStory.key)
        ? [pinnedStory, ...stories]
        : stories;
    const list = storyStatusFilter ? all.filter((s) => s.status === storyStatusFilter) : all;
    // Epic filtering is done server-side in loadStories (so it works for both
    // team-managed `parent` and company-managed "Epic Link" projects); no
    // client-side epic check here or company-managed stories would be hidden.
    return [...list].sort((a, b) => {
      const n = (k) => parseInt((k || "").split("-").pop(), 10) || 0;
      return storySortAsc ? n(a.key) - n(b.key) : n(b.key) - n(a.key);
    });
  }, [stories, pinnedStory, storyStatusFilter, storySortAsc]);

  // Column 3 — the Xray tests linked to the selected story card.
  const xrayTests = useMemo(() => coverage?.tests || [], [coverage]);

  /* The epic's own Xray tests, and how many of them nothing automates yet. */




  /* The execution dialog. It hosts the real CreateExecutionWizard rather than a
     simplified copy, so the inputs and the test picker are exactly what the
     Executions screen offers — just scoped to this story, with its tests
     already ticked, and without leaving the board. */
  const execDialog = execFlow ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-label="Create execution"
    >
      <div
        className="absolute inset-0 bg-overlay/50 backdrop-blur-sm"
        onClick={() => setExecFlow(null)}
      />
      {/* Near-full-screen: the picker nests four levels deep (ticket → Xray
          test → Playwright case → steps) with badges on every row, so narrow
          columns push each row onto two lines and the tree stops being
          readable. */}
      <div
        className={[
          "relative z-10 flex w-full flex-col overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-slate-200",
          // The blocked state is two sentences — it should not open a
          // full-height dialog around them.
          execFlow.blocked
            ? "max-w-xl"
            : `${DIALOG_HEIGHT} max-w-[92rem]`,
        ].join(" ")}
      >
        <div className="flex flex-none items-center gap-3 bg-brand-700 px-5 py-3.5 text-white">
          <Icons.Play className="h-4 w-4 flex-none" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Create execution</p>
            <p className="truncate text-xs text-brand-100">
              <span className="font-mono font-semibold">{execFlow.story?.key}</span>
              {execFlow.story?.summary ? ` — ${execFlow.story.summary}` : ""}
            </p>
          </div>
          {/* <button
            type="button"
            onClick={() => setExecFlow(null)}
            aria-label="Close"
            className="flex-none rounded-md p-1 text-brand-100 hover:bg-white/10 hover:text-white"
          >
            <Icons.X className="h-5 w-5" />
          </button> */}
        </div>

        {/* A flex column, not a scroller: the wizard fills it and scrolls its
            own step body, so its Back / Create buttons stay pinned to the
            bottom of the dialog instead of floating halfway up it. */}
        <div className="flex min-h-0 flex-1 flex-col p-4">
          {execFlow.blocked ? (
            <>
              <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <Icons.Alert className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
                <p className="text-sm leading-relaxed text-amber-900">
                  {execFlow.blocked}
                </p>
              </div>
              {/* Same footer treatment as the wizard's: divider, full-bleed,
                  pinned to the bottom edge. */}
              <div className="-mx-4 -mb-4 mt-4 flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3.5">
                <button
                  type="button"
                  onClick={() => setExecFlow(null)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <CreateExecutionWizard
              embedded
              userkey={userkey}
              initialName={execFlow.name}
              initialDescription={execFlow.description}
              initialSelected={execFlow.selected}
              initialLinkTickets={execFlow.linkTickets}
              scopeTicketKeys={execFlow.scopeTicketKeys}
              onCancel={() => setExecFlow(null)}
              onCreated={() => {
                setExecFlow(null);
                // Refresh in place — the point of doing this here is to stay on
                // the board with the epic and story still selected.
                loadLinks();
              }}
            />
          )}
        </div>
      </div>
    </div>
  ) : null;

  // Entered a story card: show its Xray tests (with the mapping grid) and
  // executions on their own screen.
  if (storyDetailOpen && selectedStoryObj) {
    return (
      <StoryDetail
        story={selectedStoryObj}
        xrayTests={xrayTests}
        executions={executions}
        coverageError={coverage?.xray_error || ""}
        coverageLoading={coverageLoading}
        executionsLoading={executionsLoading}
        userkey={userkey}
        onBack={() => setStoryDetailOpen(false)}
        onCreateXray={() =>
          onCreateXray?.({
            projectKey: selectedProject,
            epicKey: selectedStoryObj?.epic_key || "",
            story: selectedStoryObj,
          })
        }
        onCreatePlaywright={() =>
          onCreatePlaywright?.({
            projectKey: selectedProject,
            epicKey: selectedStoryObj?.epic_key || "",
            story: selectedStoryObj,
          })
        }
        onCreateExecution={() => startExecution(selectedStoryObj)}
        execDialog={execDialog}
      />
    );
  }

  /* Resuming a remembered story: hold here until its record arrives rather
     than flashing the board and then jumping into the detail screen. */
  if (storyDetailOpen && selectedStory && (storiesLoading || stories.length === 0)) {
    return (
      <div className="h-[calc(100vh-8rem)] min-h-0">
        <Loader.Overlay
          variant="guide"
          label={`Opening ${selectedStory}…`}
          sublabel="Fetching the story, its tests and Artifacts"
          delay={0}
        />
      </div>
    );
  }

  /* Refresh reloads the whole board, not just the right-hand panel: the Jira
     connection, the project list, this project's epics and story cards, the
     local ticket anchors, and the selected story's Xray tests, Playwright
     scripts and executions — the last of those with the discovery cache
     bypassed so it is genuinely re-read from Jira and Xray.

     The button's spinner tracks `refreshing`, not whether anything on the page
     happens to be loading. Selecting a story card also fetches — that is what
     the column spinners and the panel's own "Discovering Xray tests…" are for.
     Spinning Refresh then claimed the user had pressed a button they hadn't. */
  const refreshAll = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const jobs = [loadStatus()];
      if (status?.connected) {
        jobs.push(loadProjects(), loadLinks());
        if (selectedProject) {
          jobs.push(loadEpics(selectedProject));
          if (selectedEpic) jobs.push(loadStories(selectedProject, selectedEpic));
        }
        if (selectedStory) jobs.push(loadCoverage(selectedStory, { refresh: true }));
        if (selectedEpic) jobs.push(loadEpicCoverage(selectedEpic));
        setRefreshNonce((n) => n + 1);
      }
      await Promise.all(jobs);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col">
      {/* Row 1 — header */}
      <div className="mb-3">
        <h1 className="text-xl font-bold tracking-tight text-slate-900">Requirement Board</h1>
        {/* <p className="mt-0.5 text-sm text-slate-500">
          Epics → story cards. Select a story to see its tests &amp; executions, or enter it for the full step mapping.
        </p> */}
      </div>

      {/* Row 2 — project · global search · refresh */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {projects.length > 0 && (
          <select
            value={selectedProject}
            onChange={(e) => {
              setSelectedProject(e.target.value);
              setProjectLoading(true);
              setLoadingFor({ kind: "project", key: e.target.value });
            }}
            title="Jira project"
            className="h-[38px] flex-none rounded-lg border border-slate-200 bg-card px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {projects.map((p) => (
              <option key={p.key} value={p.key}>
                {p.key} — {p.name}
              </option>
            ))}
          </select>
        )}

        {/* One search over the project's epics, stories and tests. Picking a
            result selects it on the board (a test selects its story too). */}
        <BoardSearch
          project={selectedProject}
          disabled={!status?.connected || !selectedProject}
          onPick={pickFromSearch}
        />

        {/* Refresh — re-fetch epics, story cards, coverage & executions */}
        <button
          type="button"
          onClick={refreshAll}
          disabled={refreshing}
          title="Refresh"
          className="inline-flex h-[38px] flex-none items-center gap-2 rounded-lg border border-slate-200 bg-card px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
        >
          <Icons.Refresh className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* {notConnected && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Jira isn't connected. Connect Jira in Application Configuration to load epics and
          story cards. {status?.email ? `(${status.email})` : ""}
        </div>
      )} */}

      {/* {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )} */}

      {/* Xray tests attached to no epic and no story card. Project-wide and
          above the columns, because no selection would ever reveal them —
          walking epics and stories is exactly how the board misses them. */}
      {/* {status?.connected && selectedProject && orphansLoading && !orphans && (
        <p className="mb-3 flex flex-none items-center gap-2 px-1 text-[11px] text-slate-400">
          <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
          Scanning {selectedProject} for tests that are linked to nothing…
        </p>
      )} */}
      {/* {status?.connected && selectedProject && orphanTests.length > 0 && (
        <div className="mb-3 flex-none overflow-hidden rounded-xl border border-amber-300 bg-amber-50">
          <button
            type="button"
            onClick={() => setOrphansOpen((v) => !v)}
            aria-expanded={orphansOpen}
            className="flex w-full flex-wrap items-center gap-2 px-4 py-2 text-left hover:bg-amber-100/60"
          >
            <Icons.ChevronRight
              className={[
                "h-4 w-4 flex-none text-amber-600 transition-transform",
                orphansOpen ? "rotate-90" : "",
              ].join(" ")}
            />
            <Icons.Alert className="h-4 w-4 flex-none text-amber-600" />
            <span className="min-w-0 flex-1 text-xs font-semibold text-amber-900">
              {orphanTests.length} test{orphanTests.length === 1 ? "" : "s"} in{" "}
              <span className="font-mono">{selectedProject}</span> {orphanTests.length === 1 ? "is" : "are"}{" "}
              linked to no epic and no story card
            </span>
            {orphanTestsFree.length > 0 && (
              <span className="flex-none rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-900">
                {orphanTestsFree.length} not automated
              </span>
            )}
            {orphans?.truncated && (
              <span className="flex-none text-[10px] text-amber-700">
                first {orphans.scanned} scanned
              </span>
            )}
          </button>
          {orphansOpen && (
            <div className="border-t border-amber-200 bg-card">
              <div className="flex flex-wrap items-center gap-2 border-b border-amber-100 bg-amber-50/50 px-4 py-2">
                <p className="min-w-0 flex-1 text-[11px] text-amber-900">
                  These were authored and then left unattached, so nothing on this
                  board surfaces them. Link them to a story in Jira, or automate them
                  straight from here.
                </p>
                {orphanTestsFree.length > 0 && (
                  <button
                    type="button"
                    onClick={() => buildFromOrphans(orphanTestsFree.map((t) => t.key))}
                    className="inline-flex flex-none items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-50"
                  >
                    <Icons.Play className="h-3 w-3" /> Build Artifacts for all{" "}
                    {orphanTestsFree.length}
                  </button>
                )}
              </div>
              <ul className="cq-scroll max-h-56 divide-y divide-slate-100 overflow-y-auto">
                {orphanTests.map((t) => (
                  <li key={t.key} className="flex flex-wrap items-center gap-2 px-4 py-2">
                    <span className="flex-none rounded bg-blue-100/70 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-700">
                      {t.key}
                    </span>
                    <span
                      className="min-w-0 flex-1 truncate text-xs text-slate-700"
                      title={t.summary || ""}
                    >
                      {plainTitle(t.summary, selectedProject) || "(no summary)"}
                    </span>
                    {t.containers?.length > 0 && (
                      <span
                        className="flex-none text-[10px] text-slate-400"
                        title={`In ${t.containers.join(", ")} — but that isn't linked to a requirement either`}
                      >
                        in {t.containers.length} set/plan
                      </span>
                    )}
                    <span className="flex-none text-[10px] text-slate-400">
                      {t.steps_count} step{t.steps_count === 1 ? "" : "s"}
                    </span>
                    {t.automated ? (
                      <span className="inline-flex flex-none items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        <Icons.Check className="h-3 w-3" /> Automated
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => buildFromOrphans([t.key])}
                        title={`Build an Artifact for ${t.key}`}
                        className="inline-flex flex-none items-center gap-1 rounded-md border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-blue-700 hover:bg-blue-50"
                      >
                        <Icons.Play className="h-2.5 w-2.5" /> Build Artifact
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )} */}

      {/* Two columns (Epics, Story cards) + a summary panel for the story */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        {/* Left rail: Epics + Story cards */}
        {/* The two picker columns. Fixed-ish so the detail panel takes the
            slack, but they grow a little on large displays rather than leaving
            story titles truncated while the panel has room to spare. The two
            columns split the rail evenly — epic titles wrap just as badly as
            story titles when one of them is starved. */}
        <div className="flex min-h-0 flex-none flex-col gap-4 sm:flex-row lg:w-[36rem] xl:w-[42rem] 3xl:w-[48rem]">
        {/* Column 1 — Epics */}
        <Column
          icon={Icons.JiraEpic}
          plainIcon
          title="Epics"
          accent="blue"
          busy={epicsLoading && epics.length > 0}
          count={filteredEpics.length}
          onSort={() => setEpicSortAsc((v) => !v)}
          sortAsc={epicSortAsc}
          // onAdd={() => onCreateEpic?.({ projectKey: selectedProject })}
          // addDisabled={true}
          // addTitle="Create new Epic / Story cards"
        >
          {statusLoading || (status?.connected && epicsLoading && !epics.length) ? (
            <Loading text="Loading epics…" hide={projectLoading} />
          ) : !status?.connected ? (
            <EmptyState icon={Icons.Layers} text="Connect Jira to see epics." />
          ) : filteredEpics.length === 0 ? (
            <EmptyState icon={Icons.Layers} text="No epics in this project." />
          ) : (
            filteredEpics.map((ep) => {
              const active = ep.key === selectedEpic;
              return (
                <button
                  key={ep.key}
                  type="button"
                  onClick={() => setSelectedEpic(ep.key)}
                  className={[
                    "flex w-full flex-col gap-1 rounded-xl border p-3 text-left transition",
                    active
                      ? "border-blue-400 bg-blue-50 ring-1 ring-blue-400/40"
                      : "border-slate-200 bg-card hover:border-blue-200 hover:bg-blue-50/40",
                  ].join(" ")}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-blue-600">{ep.key}</span>
                    <JiraStatusBadge status={ep.status} category={ep.status_category} title={issueStatusNote("Epic", ep.key, ep.status)} />
                    <PriorityBadge priority={ep.priority} />
                  </div>
                  <span className="text-sm font-medium leading-snug text-slate-800">
                    {ep.summary || "(no summary)"}
                  </span>
                </button>
              );
            })
          )}
        </Column>

        {/* Column 2 — Stories (all, Testing status) */}
        <Column
          icon={Icons.JiraStory}
          plainIcon
          title="Stories"
          accent="blue"
          busy={storiesLoading && stories.length > 0}
          count={status?.connected ? filteredStories.length : undefined}
          onSort={() => setStorySortAsc((v) => !v)}
          sortAsc={storySortAsc}
          footer={
            <div className="border-t border-slate-200 px-3 py-2 text-[11px] text-slate-500">
              {storyStatusFilter ? (
                <>
                  Status <span className="font-medium text-slate-600">{storyStatusFilter}</span>
                </>
              ) : (
                <>All statuses</>
              )}
              {selectedEpicObj && (
                <>
                  {" "}· epic <span className="font-mono text-slate-600">{selectedEpicObj.key}</span>
                </>
              )}
            </div>
          }
        >
          {statusLoading || (status?.connected && storiesLoading && !stories.length) ? (
            <Loading text="Loading stories…" hide={projectLoading} />
          ) : !status?.connected ? (
            <EmptyState icon={Icons.Boards} text="Connect Jira to see stories." />
          ) : !selectedEpic && !pinnedStory ? (
            <EmptyState icon={Icons.Boards} text="Select an epic to see its stories." />
          ) : filteredStories.length === 0 ? (
            <EmptyState
              icon={Icons.Boards}
              text={
                selectedEpic || storyStatusFilter
                  ? "No stories match the filter."
                  : "No stories."
              }
            />
          ) : (
            /* No per-card Enter arrow — the Tests column header now carries
               a single "Enter →" that opens the current story's full
               detail. Keeps the Story cards visually consistent with Epic
               cards, which are also click-only. */
            filteredStories.map((s) => {
              const active = s.key === selectedStory;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => selectStory(s)}
                  title="Show this story's tests"
                  className={[
                    "flex w-full flex-col gap-1 rounded-xl border p-3 text-left transition",
                    active
                      ? "border-blue-400 bg-blue-50 ring-1 ring-blue-400/40"
                      : "border-slate-200 bg-card hover:border-blue-200 hover:bg-blue-50/40",
                  ].join(" ")}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-blue-600">{s.key}</span>
                    <JiraStatusBadge status={s.status} category={s.status_category} title={issueStatusNote("Story", s.key, s.status)} />
                    <PriorityBadge priority={s.priority} />
                  </div>
                  <span className="text-sm font-medium leading-snug text-slate-800">
                    {s.summary || "(no summary)"}
                  </span>
                  {s.assignee && (
                    <span className="text-[11px] text-slate-400">{s.assignee}</span>
                  )}
                </button>
              );
            })
          )}
        </Column>

        </div>

        {/* Right: the selected story's Xray tests + executions. No outer card
            chrome of its own — the Tests block inside IS its own Column-shaped
            container (bordered slate box), so wrapping it in a second white
            card produced a nested-card look that didn't match Epic / Story
            Cards on the left. */}
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Xray tests that hang off the epic instead of a story card. Always
              reported when an epic is selected — including "there are none",
              because the useful answer is knowing either way rather than
              wondering whether the board simply did not look. */}
          {/* {selectedEpic && (
            <div className="flex-none border-b border-slate-200 bg-blue-50/30">
              {epicCoverageLoading ? (
                <p className="flex items-center gap-2 px-4 py-2 text-[11px] text-slate-500">
                  <Icons.Spinner className="h-3.5 w-3.5 animate-spin text-slate-400" />
                  Checking {selectedEpic} for tests of its own…
                </p>
              ) : epicTests.length === 0 ? (
                <p className="px-4 py-2 text-[11px] text-slate-400">
                  No tests belong to epic{" "}
                  <span className="font-mono font-semibold text-slate-500">
                    {selectedEpic}
                  </span>{" "}
                  itself — every test here belongs to a story card.
                  {epicCoverage?.error ? ` (${epicCoverage.error})` : ""}
                </p>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setEpicTestsOpen((v) => !v)}
                    aria-expanded={epicTestsOpen}
                    className="flex w-full flex-wrap items-center gap-2 px-4 py-2 text-left hover:bg-blue-50/60"
                  >
                    <Icons.ChevronRight
                      className={[
                        "h-3.5 w-3.5 flex-none text-blue-400 transition-transform",
                        epicTestsOpen ? "rotate-90" : "",
                      ].join(" ")}
                    />
                    <Icons.ShieldCheck className="h-3.5 w-3.5 flex-none text-blue-600" />
                    <span className="min-w-0 flex-1 text-[11px] font-semibold text-blue-900">
                      {epicTests.length} test{epicTests.length === 1 ? "" : "s"} under
                      epic <span className="font-mono">{selectedEpic}</span>, not on any
                      story card
                    </span>
                    {epicTestsFree.length > 0 ? (
                      <span className="flex-none rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                        {epicTestsFree.length} not automated
                      </span>
                    ) : (
                      <span className="flex-none rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                        all automated
                      </span>
                    )}
                  </button>

                  {epicTestsOpen && (
                    <div className="border-t border-blue-100">

                      {epicCoverage?.no_xray_count > 0 && (
                        <p className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-[10px] leading-relaxed text-amber-900">
                          {epicCoverage.no_xray_count} of these exist in Jira but have no
                          test steps behind them, so they
                          can't be automated yet. That usually means tests aren't enabled
                          for this project, or they were created as plain Jira issues of
                          type Test with no test steps.
                        </p>
                      )}
                      {epicBuildable.length > 0 && (
                        <div className="flex items-center gap-2 px-4 py-2">
                          <p className="min-w-0 flex-1 text-[10px] text-slate-500">
                            These are authored test designs with no Artifact
                            behind them yet.
                          </p>
                          <button
                            type="button"
                            onClick={() => buildFromEpic(epicBuildable.map((t) => t.key))}
                            className="flex-none inline-flex items-center gap-1 rounded-md border border-blue-200 bg-white px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-50"
                          >
                            <Icons.Play className="h-3 w-3" /> Build Artifacts for all{" "}
                            {epicBuildable.length}
                          </button>
                        </div>
                      )}
                      <ul className="max-h-64 divide-y divide-blue-100 overflow-y-auto border-t border-blue-100">
                        {epicTests.map((t) => (
                          <li
                            key={t.key}
                            className="flex flex-wrap items-center gap-2 bg-card px-4 py-2"
                          >
                            <span className="flex-none rounded bg-blue-100/70 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-700">
                              {t.key}
                            </span>
                            <span
                              className="min-w-0 flex-1 truncate text-xs text-slate-700"
                              title={t.summary || ""}
                            >
                              {plainTitle(t.summary, selectedEpic) || "(no summary)"}
                            </span>
                            <span
                              className="flex-none rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500"
                              title={
                                t.attachment === "parent"
                                  ? `${t.key}'s parent is this epic`
                                  : `${t.key} is linked to this epic`
                              }
                            >
                              {t.attachment === "parent" ? "child of epic" : "linked"}
                            </span>
                            <span className="flex-none text-[10px] text-slate-400">
                              {t.steps_count} step{t.steps_count === 1 ? "" : "s"}
                            </span>
                            {t.automated ? (
                              <span className="inline-flex flex-none items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                <Icons.Check className="h-3 w-3" /> Automated
                              </span>
                            ) : t.xray_known === false ? (
                              <span
                                className="flex-none rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                                title="Jira has this issue but no test steps behind it, so there is nothing to automate"
                                >
                                no test steps
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => buildFromEpic([t.key])}
                                title={`Build an Artifact for ${t.key}`}
                                className="inline-flex flex-none items-center gap-1 rounded-md border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-blue-700 hover:bg-blue-50"
                              >
                                <Icons.Play className="h-2.5 w-2.5" /> Build Artifact
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )} */}

          {!selectedStory ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-10 text-center">
              <Icons.JiraStory muted />
              <p className="text-sm text-slate-400">
                Select a story to see its tests.
              </p>
            </div>
          ) : (
            <>
              {/* Story header */}
              {/* <div className="flex flex-none flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3">
                <span className="font-mono text-xs font-bold text-blue-700">
                  {selectedStoryObj?.key || selectedStory}
                </span>
                {selectedStoryObj?.status && (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ring-1 ring-inset ${statusBadgeClasses(
                      selectedStoryObj.status_category
                    )}`}
                  >
                    {selectedStoryObj.status}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                  {selectedStoryObj?.summary || ""}
                </span>
                <button
                  type="button"
                  onClick={() => setStoryDetailOpen(true)}
                  className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Enter <Icons.ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div> */}

              {/* Tests acts as a third Column, side-by-side with Epic / Story
                  Cards. Mirrors their <section> structure exactly:
                    flex min-h-0 flex-1 flex-col  → fills the right panel height
                    bg-slate-50/60                → same slate container tint
                    sticky slate-100/70 header    → "only header a little darker"
                    cq-scroll flex-1 overflow-y-auto body → fixed outer height,
                                                            inner scroll when
                                                            there are more tests
                  The intermediate scrollable p-4 wrapper that used to sit here
                  is gone — it was what kept the Tests block floating at
                  content-height instead of filling the column. */}
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60 shadow-sm">
                  <header className="flex flex-none items-center gap-2 border-b border-slate-200 bg-slate-100/70 px-3 py-2.5">
                    <Icons.JiraXrayTest />
                    <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                      Tests
                    </h3>
                    <span className="flex-none rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-500 ring-1 ring-slate-200">
                      {xrayTests.length}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onCreatePlaywright?.({
                          projectKey: selectedProject,
                          epicKey: selectedStoryObj?.epic_key || "",
                          story: selectedStoryObj,
                        })
                      }
                      disabled={xrayTests.length === 0}
                      title={
                        xrayTests.length === 0
                          ? "Add tests first — Artifact scripts are built from them"
                          : "Create Artifact scripts from this story's tests"
                      }
                      className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-blue-600 bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:border-blue-700 hover:bg-blue-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
                    >
                      <Icons.Plus className="h-4 w-4" /> Create Artifact
                    </button>
                     <button
                      type="button"
                      onClick={() => setStoryDetailOpen(true)}
                      title="Open the full detail view for this story"
                      className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                    >
                      Review <Icons.ChevronRight className="h-4 w-4" />
                    </button>
                  </header>

                  {/* Scrollable body — fixed outer height is held by the
                      flex-1 column container above, so this is where the
                      "more tests → scroll inside the column" behaviour lives,
                      matching Epic / Story Cards. */}
                  <div className="cq-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
                  {coverageLoading ? (
                    /* The guide popup, never an inline spinner. While a popup
                       is already up (story click, back from Create Artifact)
                       that one covers the reload. */
                    projectLoading ? null : (
                      <Loader.Overlay
                        variant="guide"
                        label="Loading tests…"
                        sublabel={`Fetching tests from ${selectedStory}`}
                        delay={0}
                      />
                    )
                  ) : coverage?.xray_error ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                      Couldn't read Test for {selectedStory}: {coverage.xray_error}
                    </div>
                  ) : xrayTests.length === 0 ? (
                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400">
                      No tests linked to this story yet.
                    </p>
                  ) : (
                    /* Each row opens to the test's manual Xray steps. A step
                       count alone doesn't tell you what the test does, which
                       matters most before anything has been automated. */
                    <ul className="space-y-2">
                      {xrayTests.map((x) => {
                        const open = openXray.has(x.key);
                        const steps = x.steps || [];
                        return (
                          <li
                            key={x.key}
                            className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm transition hover:border-blue-200"
                          >
                            <button
                              type="button"
                              onClick={() => toggleXray(x.key)}
                              aria-expanded={open}
                              className="flex w-full flex-wrap items-center gap-2 px-3 py-3 text-left transition hover:bg-blue-50/40"
                            >
                              <Icons.ChevronRight
                                className={[
                                  "h-3.5 w-3.5 flex-none text-slate-400 transition-transform",
                                  open ? "rotate-90" : "",
                                ].join(" ")}
                              />
                              {/* Font scale lifted to match the Story Cards:
                                  key stays mono 11px, but the test summary is
                                  now text-sm font-medium (same as story
                                  summary), and the trailing meta chips move
                                  from 10px up to 11px so the row reads as a
                                  peer of a story card, not a smaller sub-row. */}
                              <span className="text-sm font-medium text-blue-600">{x.key}</span>
                              <span
                                className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800"
                                title={x.summary || ""}
                              >
                                {plainTitle(x.summary, selectedStory) || "(no summary)"}
                              </span>
                              {/* Jira status (Draft / In Review / Needs Update in the warning
                                  colour: their trial runs are not evidence) and priority. */}
                              <JiraStatusBadge status={x.status} category={x.status_category} title={issueStatusNote("Test", x.key, x.status)} />
                              <PriorityBadge priority={x.priority} />
                              {scenarioCount(steps) > 0 && (
                                <span className="text-[11px] text-slate-400">
                                  {scenarioCount(steps)} step{scenarioCount(steps) === 1 ? "" : "s"}
                                </span>
                              )}
                              {x.automated ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                  <Icons.Check className="h-3.5 w-3.5" /> Automated
                                </span>
                              ) : (
                                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                                  Not automated
                                </span>
                              )}
                            </button>
                            {open && (
                              <div className="border-t border-slate-100 bg-slate-50/60">
                                {(trialWarning(x.key, x.status) || (x.testcases || []).length > 0) && (
                                  <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2">
                                    {trialWarning(x.key, x.status) && (
                                      <span className="flex min-w-0 flex-1 items-start gap-1 text-[11px] text-amber-800">
                                        <Icons.Alert className="mt-px h-3 w-3 flex-none" aria-hidden="true" />
                                        {trialWarning(x.key, x.status)}
                                      </span>
                                    )}
                                    {(x.testcases || []).length > 0 && (
                                      <ActivityButton
                                        title={`${x.key} — Artifact`}
                                        scope={{ testcasekey: x.testcases[0].testcasekey }}
                                        testKey={x.key}
                                      />
                                    )}
                                  </div>
                                )}
                                {/* The Cucumber scenario only, compact for the column. */}
                                {isGherkin(steps) ? (
                                  <div className="px-3 py-2">
                                    <GherkinLines steps={steps} compact />
                                  </div>
                                ) : (
                                  <p className="px-3 py-2.5 text-xs text-slate-400">{NO_SCENARIO}</p>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  </div>
                </div>

                {/* Executions */}
                {/* <div>
                  <div className="mb-2 flex items-center gap-2">
                    <Icons.Play className="h-4 w-4 text-emerald-600" />
                    <h3 className="text-sm font-semibold text-slate-800">Executions</h3>
                    <span
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500"
                      title={
                        xrayQuery && shownExecutions.length !== executions.length
                          ? `${shownExecutions.length} of ${executions.length} shown`
                          : undefined
                      }
                    >
                      {xrayQuery && shownExecutions.length !== executions.length
                        ? `${shownExecutions.length}/${executions.length}`
                        : executions.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => startExecution(selectedStoryObj)}
                      disabled={execStarting || !!execBlockedReason}
                      title={execBlockedReason || "Create an execution for this story"}
                      className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-white disabled:text-slate-300"
                    >
                      <Icons.Plus className="h-3 w-3" /> Create Execution
                    </button>
                  </div>

                  {executionsPending ? (
                    <Loading text="Loading executions…" hide={projectLoading} />
                  ) : shownExecutions.length === 0 ? (
                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400">
                      {xrayQuery && executions.length > 0
                        ? `No executions match "${xrayQuery}".`
                        : "No executions created for this story yet."}
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {shownExecutions.map((ex) => (
                        <li key={ex.executionkey} className="rounded-lg border border-slate-200 px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                              {ex.name}
                            </span>
                            <ExecutionBadge status={ex.status} />
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-slate-400">
                            {ex.createddate && <span>{formatDate(ex.createddate)}</span>}
                            {Array.isArray(ex.report_types) && ex.report_types.length > 0 && (
                              <span className="inline-flex items-center gap-1 text-emerald-600">
                                <Icons.Report className="h-3 w-3" /> {ex.report_types.length} report
                                {ex.report_types.length === 1 ? "" : "s"}
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div> */}
            </>
          )}
        </section>
      </div>

      {execDialog}

      {projectLoading && (
        <Loader.Overlay
          variant="guide"
          /* Text follows what raised the popup (`loadingFor`): initial /
             project change, epic click (stories reload), story click (Xray
             tests reload), or a return to the board. */
          label={
            loadingFor?.kind === "return"
              ? "Loading, please wait…"
              : loadingFor?.kind === "story"
              ? "Loading tests…"
              : loadingFor?.kind === "epic"
                ? "Loading stories…"
                : "Loading project…"
          }
          sublabel={
            loadingFor?.kind === "return"
              ? "Back to Requirement Board"
              : loadingFor?.kind === "story"
              ? `Fetching tests from ${loadingFor.key}`
              : loadingFor?.kind === "epic"
                ? `Fetching stories under ${loadingFor.key}`
                : "Fetching epics from Jira"
          }
          delay={0}
        />
      )}
    </div>
  );
}
